import React, { useState, useMemo } from "react";
import { Icon } from "@iconify/react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Drawer,
  IconButton,
  AppBar,
  Toolbar,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import BarChartIcon from "@mui/icons-material/BarChart";
import LogoutIcon from "@mui/icons-material/Logout";
import SiteSetupIcon from "@mui/icons-material/Settings";
import TvIcon from "@mui/icons-material/Tv";
import PrintIcon from "@mui/icons-material/Print";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useDarkMode } from "../../hooks/useDarkMode";
import adminLogo from "../../assets/branding/MyODSLogo.png";
import APP_VERSION from "../../generated/version";

import ScreensPanel from "./ScreensPanel";
import Reports from "./Reports";
import Dashboard from "./Dashboard";
import SiteSetup from "./SiteSetup";
import Printers from "./Printers";

import { useAuth } from "../../contexts/AuthContext";

function AdminDashboard() {
  const [activeSection, setActiveSection] = useState("Dashboard");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const { isDark, setDarkMode } = useDarkMode("reportsDarkMode");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const collapsedSidebarWidth = 88;
  const expandedSidebarWidth = 250;
  const cupLogoSrc = `${process.env.PUBLIC_URL}/myodscup.png`;

  //  Access Keycloak logout function from AuthContext
  const { logout, keycloak, hasRole } = useAuth();

  // Only show Site Setup if user can edit screens
  const canEditScreens = hasRole("screens:edit") // || hasRole("admin");

  // Build menu based on permissions
  const topMenuItems = useMemo(() => {
    const items = [
      { label: "Dashboard", icon: <DashboardIcon />, section: "Dashboard" },
      { label: "Screens", icon: <TvIcon />, section: "Screens" },
      { label: "Reports", icon: <BarChartIcon />, section: "Reports" },
    ];

    if (canEditScreens) {
      items.push({
        label: "Site Setup",
        icon: <SiteSetupIcon />,
        section: "SiteSetup",
      });
      items.push({
        label: "Printers Setup",
        icon: <PrintIcon />,
        section: "Printers",
      });
    }

    return items;
  }, [canEditScreens]);

  // If they lose permission while on SiteSetup, kick them back somewhere safe
  React.useEffect(() => {
    if (!canEditScreens && (activeSection === "SiteSetup" || activeSection === "Printers")) {
      setActiveSection("Dashboard");
    }
  }, [canEditScreens, activeSection]);

  // Dark mode is report-scoped: force light outside Reports and restore the last Reports preference when returning.
  React.useEffect(() => {
    if (activeSection === "Reports") {
      const savedReportsDark = window.localStorage.getItem("reportsDarkMode") === "true";
      setDarkMode(savedReportsDark);
      return;
    }

    // Outside Reports we always render in light mode, but we do not overwrite Reports preference.
    if (typeof window !== "undefined") {
      window.document.documentElement.classList.remove("dark");
    }
  }, [activeSection, setDarkMode]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarCollapseToggle = () => {
    setIsSidebarCollapsed((prev) => !prev);
  };

  /**
   *  Keycloak Logout (safe + reliable)
   */
  const handleSignOut = async () => {
    try {
      if (window._socket?.connected) window._socket.disconnect();

      // Check that logout() exists before calling
      if (typeof logout === "function") {
        await logout({ redirectUri: window.location.origin });
      } else {
        console.warn("logout() not available in context, using fallback...");
        const base = (process.env.REACT_APP_KEYCLOAK_URL || "").replace(/\/$/, "");
        const realm = process.env.REACT_APP_KEYCLOAK_REALM || "MyODS";
        const post = encodeURIComponent(window.location.origin);
        const idHint = keycloak?.idToken;
        const url =
          `${base}/realms/${realm}/protocol/openid-connect/logout` +
          `?post_logout_redirect_uri=${post}` +
          (idHint ? `&id_token_hint=${encodeURIComponent(idHint)}` : "");
        window.location.href = url;
      }
    } catch (e) {
      console.error("Sign out failed:", e);

      //  Fallback in case logout() throws
      try {
        const base = (process.env.REACT_APP_KEYCLOAK_URL || "").replace(/\/$/, "");
        const realm = process.env.REACT_APP_KEYCLOAK_REALM || "MyODS";
        const post = encodeURIComponent(window.location.origin);
        const idHint = keycloak?.idToken;
        const url =
          `${base}/realms/${realm}/protocol/openid-connect/logout` +
          `?post_logout_redirect_uri=${post}` +
          (idHint ? `&id_token_hint=${encodeURIComponent(idHint)}` : "");
        window.location.href = url;
      } catch (err) {
        console.error("Fallback logout failed:", err);
        window.location.href = "/";
      }
    }
  };

  const drawerContent = (
    <Box
      sx={{
        width: isMobile ? expandedSidebarWidth : (isSidebarCollapsed ? collapsedSidebarWidth : expandedSidebarWidth),
        background: "linear-gradient(to bottom, #1c1c1c, #3b4a5a)",
        height: "100%",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        p: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isSidebarCollapsed && !isMobile ? "column" : "row",
          alignItems: "center",
          justifyContent: isSidebarCollapsed && !isMobile ? "center" : "space-between",
          mb: 2,
          gap: 1,
        }}
      >
        {!(isSidebarCollapsed && !isMobile) && (
          <Box
            component="img"
            src={adminLogo}
            alt="MyODS logo"
            sx={{
              display: "block",
              width: "100%",
              maxWidth: 180,
              height: "auto",
              mx: "auto",
              flexShrink: 0,
            }}
          />
        )}
        {!isMobile && (
          <IconButton
            onClick={handleSidebarCollapseToggle}
            aria-label={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            sx={{
              color: "#d1d5db",
              flexShrink: 0,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
            }}
          >
            {isSidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        )}
        <Box
          component="img"
          src={cupLogoSrc}
          alt="MyODS logo"
          sx={{
            display: isSidebarCollapsed && !isMobile ? "block" : "none",
            width: 40,
            maxWidth: 40,
            height: "auto",
            mx: "auto",
            flexShrink: 0,
          }}
        />
      </Box>
      <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", mb: 2 }} />

      <List>
        {topMenuItems.map((item, index) => (
          <ListItem key={index} disablePadding>
            <ListItemButton
              title={isSidebarCollapsed && !isMobile ? item.label : undefined}
              selected={activeSection === item.section}
              onClick={() => {
                setActiveSection(item.section);
                if (isMobile) setMobileOpen(false);
              }}
              sx={{
                color: "#fff",
                borderRadius: "8px",
                mx: 0.5,
                mb: 1,
                minHeight: 48,
                justifyContent: isSidebarCollapsed && !isMobile ? "center" : "flex-start",
                px: isSidebarCollapsed && !isMobile ? 1.5 : 2,
                "&.Mui-selected": {
                  backgroundColor: "#4b5563",
                  "&:hover": { backgroundColor: "#6b7280" },
                },
                "&:hover": { backgroundColor: "#374151" },
              }}
            >
              <ListItemIcon
                sx={{
                  color: "#d1d5db",
                  minWidth: isSidebarCollapsed && !isMobile ? 0 : 40,
                  justifyContent: "center",
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!(isSidebarCollapsed && !isMobile) && <ListItemText primary={item.label} />}
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Box sx={{ mt: "auto" }}>
        <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", my: 2 }} />
        {!(isSidebarCollapsed && !isMobile) && <Box sx={{ px: 1.5 }}>
          <Typography
            variant="caption"
            display="block"
            sx={{ color: "rgba(255,255,255,0.8)", lineHeight: 1.4 }}
          >
            MyODS
          </Typography>
          <Typography
            variant="caption"
            display="block"
            sx={{ color: "rgba(255,255,255,0.8)", lineHeight: 1.4 }}
          >
            Version {APP_VERSION}
          </Typography>
          <Typography
            variant="caption"
            display="block"
            sx={{ color: "rgba(255,255,255,0.6)", lineHeight: 1.4 }}
          >
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
              <Icon icon="fluent-emoji:copyright" aria-hidden="true" />
              MyODS 2026
            </Box>
          </Typography>
        </Box>}
        <List>
          <ListItem disablePadding>
            <ListItemButton
              title={isSidebarCollapsed && !isMobile ? "Sign Out" : undefined}
              onClick={handleSignOut}
              sx={{
                color: "#fff",
                borderRadius: "8px",
                mx: 0.5,
                minHeight: 48,
                justifyContent: isSidebarCollapsed && !isMobile ? "center" : "flex-start",
                px: isSidebarCollapsed && !isMobile ? 1.5 : 2,
                "&:hover": { backgroundColor: "#374151" },
              }}
            >
              <ListItemIcon
                sx={{
                  color: "#d1d5db",
                  minWidth: isSidebarCollapsed && !isMobile ? 0 : 40,
                  justifyContent: "center",
                }}
              >
                <LogoutIcon />
              </ListItemIcon>
              {!(isSidebarCollapsed && !isMobile) && <ListItemText primary="Sign Out" />}
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Box>
  );

  const renderContent = () => {
    switch (activeSection) {
      case "Dashboard":
        return (
          <Box mt={2}>
            <Dashboard />
          </Box>
        );
      case "Screens":
        return (
          <Box mt={2}>
            <ScreensPanel />
          </Box>
        );
      case "Reports":
        return (
          <Box mt={2}>
            <Reports />
          </Box>
        );
      case "SiteSetup":
        return canEditScreens ? (
          <Box mt={2}>
            <SiteSetup />
          </Box>
        ) : (
          <Box mt={2}>
            <Typography>You do not have access to Site Setup.</Typography>
          </Box>
        );
      case "Printers":
        return canEditScreens ? (
          <Box mt={2}>
            <Printers />
          </Box>
        ) : (
          <Box mt={2}>
            <Typography>You do not have access to Printers.</Typography>
          </Box>
        );
      default:
        return (
          <Box mt={2}>
            <Typography>Select a section from the menu.</Typography>
          </Box>
        );
    }
  };

  return (
    <Box sx={{ display: "flex", height: "100vh", width: "100vw", overflow: "hidden" }}>
      {/* AppBar for mobile */}
      {isMobile && (
        <AppBar position="fixed" sx={{ backgroundColor: "#1c1c1c" }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div">
              MyODS
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {/* Sidebar or Drawer */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Box
          sx={{
            width: isSidebarCollapsed ? collapsedSidebarWidth : expandedSidebarWidth,
            flexShrink: 0,
            transition: "width 180ms ease",
          }}
        >
          {drawerContent}
        </Box>
      )}

      {/* Main Content */}
      <Box
        component="main"
        className="bg-gradient-to-br from-blue-50 to-white dark:from-gray-900 dark:to-gray-800"
        sx={{
          flexGrow: 1,
          p: isMobile ? 2 : 4,
          mt: isMobile ? 7 : 0,
          overflowY: "auto",
          minHeight: "100vh",
        }}
      >
        {renderContent()}
      </Box>
    </Box>
  );
}

export default AdminDashboard;
